import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/components/halloween.css';

const DISMISS_KEY = 'halloweenBannerDismissedYear';

/**
 * Halloween season window: Oct 18 → Nov 2, so it covers Halloween night and
 * Día de Muertos and then packs itself away without anyone having to remember.
 */
export const isHalloweenSeason = (date = new Date()) => {
    const month = date.getMonth();
    const day = date.getDate();
    return (month === 9 && day >= 18) || (month === 10 && day <= 2);
};

// The greeting is decorative, so a dismissal lasts the whole season instead of
// coming back every morning. localStorage throws in some privacy modes — a bit
// of seasonal fun is never worth breaking the dashboard over.
const readDismissedYear = () => {
    try {
        return window.localStorage.getItem(DISMISS_KEY);
    } catch {
        return null;
    }
};

const writeDismissedYear = (year) => {
    try {
        window.localStorage.setItem(DISMISS_KEY, year);
    } catch {
        // Dismissal just won't survive a reload; the banner still closes now.
    }
};

/** Seasonal greeting banner — same shape as the birthday banner, spookier palette. */
export function HalloweenBanner() {
    const { t } = useTranslation();
    const season = String(new Date().getFullYear());
    const [visible, setVisible] = useState(() => readDismissedYear() !== season);

    if (!visible) return null;

    const dismiss = () => {
        writeDismissedYear(season);
        setVisible(false);
    };

    return (
        <div className="halloween-banner">
            <div className="halloween-banner-content">
                <span className="halloween-banner-icon">🎃</span>
                <div className="halloween-banner-text">
                    <div className="halloween-banner-title">{t('halloween_title')}</div>
                    <div className="halloween-banner-subtitle">{t('halloween_subtitle')}</div>
                </div>
            </div>
            <button
                className="halloween-close-btn"
                onClick={dismiss}
                aria-label={t('halloween_dismiss')}
                title={t('halloween_dismiss')}
            >
                ✕
            </button>
        </div>
    );
}

// ── Cobweb geometry ─────────────────────────────────────────────────────────
// A corner web is radial strands from the corner plus rings that sag between
// them. Generating it beats hand-writing path data and keeps it tweakable.
const WEB_ANGLES = [0, 22.5, 45, 67.5, 90];
const WEB_RINGS = [38, 68, 98, 128];
const SAG = 0.88;

const polar = (r, deg) => {
    const rad = (deg * Math.PI) / 180;
    return [r * Math.cos(rad), r * Math.sin(rad)];
};

const ringPath = (r) =>
    WEB_ANGLES.slice(1)
        .map((angle, i) => {
            const [x, y] = polar(r, angle);
            const [cx, cy] = polar(r * SAG, (WEB_ANGLES[i] + angle) / 2);
            return `Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(' ');

function Cobweb({ className }) {
    const outer = WEB_RINGS[WEB_RINGS.length - 1];
    return (
        <svg className={className} viewBox="0 0 150 150" width="150" height="150" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
                {WEB_ANGLES.map((angle) => {
                    const [x, y] = polar(outer, angle);
                    return <line key={angle} x1="0" y1="0" x2={x.toFixed(1)} y2={y.toFixed(1)} />;
                })}
                {WEB_RINGS.map((r) => (
                    <path key={r} d={`M ${r} 0 ${ringPath(r)}`} />
                ))}
            </g>
        </svg>
    );
}

/**
 * Ambient decoration layer: cobwebbed corners, a dangling spider and a few bats
 * drifting past. Purely visual — it never swallows a click.
 */
export function HalloweenDecor() {
    return (
        <div className="hw-decor" aria-hidden="true">
            <Cobweb className="hw-web hw-web--left" />
            <Cobweb className="hw-web hw-web--right" />

            <div className="hw-spider">
                <span className="hw-spider-thread" />
                <span className="hw-spider-body">🕷️</span>
            </div>

            <span className="hw-bat hw-bat--1">🦇</span>
            <span className="hw-bat hw-bat--2">🦇</span>
            <span className="hw-bat hw-bat--3">🦇</span>
        </div>
    );
}
